import { useCallback } from 'react';
import { useService } from '@atlassian/jira-common-services/src/use-service/index.tsx';
import { trackFetchErrors } from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';

import type {
	WorkspaceId,
	CmdbObjectId,
} from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';

import { getObjectDetailsUrl } from '../../common/utils.tsx';
import {
	transformBackendValueToCmdbObject,
	transformInsightServiceObjectToCmdbObjectResponse,
} from '../utils.tsx';
import type { ObjectDetailsService, Response } from './types.tsx';

// @deprecated this is currently only used for the object details panel in the jira cmdb field
// which will be replaced at some point and is not intended to be used in other parts of the insight ui
export const useObjectDetailsService = (): ObjectDetailsService => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const request = useCallback(
		// @ts-expect-error - TS7031 - Binding element 'objectId' implicitly has an 'any' type. | TS7031 - Binding element 'workspaceId' implicitly has an 'any' type.
		({ objectId, workspaceId }) =>
			performGetRequest(getObjectDetailsUrl(workspaceId, objectId))
				.then(transformInsightServiceObjectToCmdbObjectResponse)
				.then(transformBackendValueToCmdbObject)
				.then((cmdbObject) => {
					fireTrackAnalytics(createAnalyticsEvent({}), 'fetchObjectDetails succeeded');
					return cmdbObject;
				})
				.catch((error) => {
					trackFetchErrors({
						error,
						id: 'fetchObjectDetails',
						packageName: 'jiraServicedeskInsightObjectServices',
					});
					throw error;
				}),
		[createAnalyticsEvent],
	);

	const {
		loading,
		error,
		data,
		fetch: fetchObjectDetails,
	} = useService<
		Response,
		{
			objectId: CmdbObjectId;
			workspaceId: WorkspaceId;
		}
	>(request);

	return [
		{
			loading,
			error,
			data,
		},
		fetchObjectDetails,
	];
};
